import _ from 'lodash';
import { lsApiService, lsErrorService, lsStorageService } from 'ls-vue-common';
import router from '@/router';
import {
  AUTH__MUTATE_CUSTOMER,
  AUTH__MUTATE_USER,
  DISPUTE__INITIALIZE_CURRENT_DISPUTE,
  PROPERTIES__MUTATE_APPLICATION_REVIEW_PROPERTY_ID,
  REVIEW__GET_TIPPING_OPTIONS,
  REVIEW__MUTATE_LOADING_STATUS,
  REVIEW__MUTATE_TIPPING_OPTIONS,
  REVIEW__RESET,
  REVIEW__REVIEW_SERVICE,
  SCHEDULES__GET_SCHEDULE,
  SERVICE_HISTORY__MUTATE_SCHEDULE,
} from '@/store/types';
import { lsModuleHelpers } from '../helpers';

export const createInitialState = () => {
  return {
    loadingStatus: {
      isLoading: false,
      isUpdating: false,
    },
    tippingOptions: {
      show: null,
      amounts: [],
      percentages: [],
    },
  };
};

export const mutations = {
  [REVIEW__RESET](state) {
    lsModuleHelpers.resetState({ state, createInitialState });
  },
  [REVIEW__MUTATE_TIPPING_OPTIONS](state, payload) {
    _.assign(state.tippingOptions, payload);
  },
  [REVIEW__MUTATE_LOADING_STATUS](state, payload) {
    _.assign(state.loadingStatus, payload);
  },
};

export const actions = {
  [REVIEW__REVIEW_SERVICE](
    { commit, rootState, dispatch },
    {
      property_id,
      schedule_id,
      scheduleevent_id,
      rating,
      issue_type,
      notes,
      share_with_support,
      tip_show,
      tip_amount,
      tip_percentage,
      onSuccess,
    }
  ) {
    const { serviceHistory, auth } = rootState;
    const { customer_id, customer } = auth.user;

    commit(REVIEW__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/schedules/${schedule_id}/scheduleevents/${scheduleevent_id}/reviews`,
        payload: {
          issue_type,
          rating,
          share_with_support,
          notes,
          // screen should handle nullifying values correctly, but for sanity we'll do it again here
          tip_show,
          tip_amount: rating > 3 && _.isNumber(tip_amount) ? tip_amount : null,
          tip_percentage: rating > 3 && _.isNumber(tip_percentage) ? tip_percentage : null,
        },
      })
      .then(({ data } = {}) => {
        if (rating === 5 && !_.get(data, 'cycle_change_offered_at')) {
          router.push({ name: data.user.offer_app_review ? 'services' : 'share' });
        } else if (
          _.get(data, 'scheduleEvent.is_completion_disputable') &&
          issue_type &&
          issue_type !== 'property_damage'
        ) {
          dispatch(DISPUTE__INITIALIZE_CURRENT_DISPUTE, {
            property_id,
            schedule_id,
            scheduleevent_id,
            scheduleevent_review_id: _.get(data, 'scheduleevent_review_id'),
            schedule_event: _.get(data, 'scheduleEvent'),
            dispute_type: issue_type,
            redistributionOffered: Boolean(_.get(data, 'redistribution_offered_at')),
          });
        } else if (issue_type === 'property_damage') {
          router.push({ name: 'propertyDamage' });
        }

        commit(PROPERTIES__MUTATE_APPLICATION_REVIEW_PROPERTY_ID, property_id);

        lsStorageService.set('APPLICATION_REVIEW_DISMISSED', false);

        commit(AUTH__MUTATE_USER, data.user);

        commit(AUTH__MUTATE_CUSTOMER, {
          last_completed_scheduleevent: {
            ...customer.last_completed_scheduleevent,
            is_reviewable: false,
          },
        });
        dispatch(SCHEDULES__GET_SCHEDULE, {
          property_id,
          schedule_id,
          forceRefresh: true,
        });

        if (_.get(serviceHistory.schedule, 'schedule_id') === schedule_id) {
          const schedule = _.cloneDeep(serviceHistory.schedule);
          const idx = _.findIndex(schedule.scheduleevents, { scheduleevent_id });
          schedule.scheduleevents[idx].reviews.push({ rating });
          commit(SERVICE_HISTORY__MUTATE_SCHEDULE, schedule);
        }

        onSuccess(data);
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(REVIEW__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [REVIEW__GET_TIPPING_OPTIONS]({ commit, rootState }, { property_id, schedule_id }) {
    const { customer_id } = rootState.auth.user;

    commit(REVIEW__MUTATE_LOADING_STATUS, {
      isLoading: true,
    });

    return lsApiService
      .request({
        method: 'GET',
        route: `/customers/${customer_id}/properties/${property_id}/schedules/${schedule_id}/tipping`,
      })
      .then(({ data }) => {
        commit(REVIEW__MUTATE_TIPPING_OPTIONS, data);
      })
      .catch((error) => {
        lsErrorService.log({ error });
      })
      .finally(() => {
        commit(REVIEW__MUTATE_LOADING_STATUS, {
          isLoading: false,
        });
      });
  },
};

export const getters = {};

export const review = {
  state: createInitialState,
  mutations,
  actions,
  getters,
};
